import { useMutation } from "@apollo/client/react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { StatefulButton } from "../../../components/ui/muiButtons";
import { RemoveUserV2Mutation, RemoveUserV2Document, RemoveUserV2MutationVariables } from "../../../graphql/types";

interface IDeactivateUser {
  showModal: boolean;
  closeModal: () => void;
  userName: string;
  userEmail: string;
}

const DeactivateUser = ({ showModal, closeModal, userName, userEmail }: IDeactivateUser) => {
  const navigate = useNavigate();
  const [deactivateUser, deactivateUserState] = useMutation<RemoveUserV2Mutation, RemoveUserV2MutationVariables>(
    RemoveUserV2Document,
    {
      onCompleted: (result: RemoveUserV2Mutation) => {
        if (result.removeUserV2.__typename === "ResultOk") {
          toast.success("User deactivated");
          navigate("/admin?currentTab=users");
        } else if (result.removeUserV2.__typename === "RemoveUserV2Errors") {
          result.removeUserV2.errors?.forEach((error) => {
            if (error.__typename === "AttachedToMoreAccountsError") {
              toast.error(error.message);
            }
          });
        }
      },
      onError: () => {
        toast.error("Error when deactivating user");
      },
    }
  );
  const handleDeactivateUser = () => {
    deactivateUser({
      variables: {
        email: userEmail,
      },
    });
  };

  const {
    control,
    formState: { isValid },
    reset,
  } = useForm<{
    deactivateField: string;
  }>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      deactivateField: "",
    },
  });
  return (
    <form>
      <Dialog
        open={showModal}
        data-testid="confirm-deactivate-dialog"
        onClose={() => {
          closeModal();
          reset();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle variant="heading.four">
          Deactivate <b>{userName}</b>?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "14px" }}>
            <b>This action cannot be undone.</b>
            <br />
            To confirm, please type <b>deactivate </b> below
          </DialogContentText>
          <Controller
            name="deactivateField"
            control={control}
            rules={{
              required: true,
              validate: (value) => value.toLocaleLowerCase() === "deactivate",
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                error={Boolean(fieldState.error)}
                required
                id="deactivateField"
                type="text"
                autoFocus
                autoComplete="off"
                margin="dense"
                placeholder="Type deactivate"
                fullWidth
                variant="standard"
                aria-label="Type deactivate"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeModal()} variant="outlined">
            Cancel
          </Button>
          <StatefulButton
            onClick={handleDeactivateUser}
            loading={deactivateUserState.loading}
            disabled={!isValid}
            color="error"
          >
            Deactivate
          </StatefulButton>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export { DeactivateUser };
